"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { AttributionDashboard } from "./attribution-dashboard/attribution-dashboard"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, ShoppingBag } from "lucide-react"
import { resolveUserTimezone, shopifyPeriodToDateRange } from "@/lib/date-utils"
import { ChannelConfigDialog } from "./attribution-dashboard/dialogs/channel-config-dialog"
import { useShopifyAttributionSettings } from "@/hooks/use-tenant-settings"
import { useAttributionModelPreferenceQuery } from "@/hooks/queries/attribution-model-preference"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { INTEGRATIONS_PERMISSIONS, SHOPIFY_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useShopify } from "@/hooks/use-shopify"
import { useShopifyInsightsAccess, type ShopifyInsightsMode } from "@/hooks/use-shopify-insights-access"
import { InsightsLockedNotice, InsightsTrialBanner } from "./shopify-insights-gate"
import { RevenueSummaryView } from "./attribution-dashboard/revenue-summary-view"
import { ProductFilterSideTab } from "./attribution-dashboard/product-filter-side-tab"
import { DEFAULT_SHOPIFY_PERIOD, DEFAULT_COMPARISON_MODE, type ShopifyPeriod, type ComparisonMode } from "./attribution-dashboard/period-constants"
import type { ProductOption } from "@/types/shopify"
import { useTranslations } from "next-intl"


export function ShopifyDashboardPage() {
  const tUi = useTranslations("Shopify.ShopifyDashboardPage")
  const tCommonUi = useTranslations("Common")

  const { user } = useAuth()
  const { activeIntegrations, isLoading } = useShopify()
  const insightsAccess = useShopifyInsightsAccess(true)
  const [selectedIntegration, setSelectedIntegration] = useState<string>("")
  const userTimezone = resolveUserTimezone(user)
  const [period, setPeriod] = useState<ShopifyPeriod>(DEFAULT_SHOPIFY_PERIOD)
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null)
  const dateRange = useMemo(
    () => ({
      ...shopifyPeriodToDateRange(period, userTimezone, customRange ?? undefined),
      timezone: userTimezone,
    }),
    [period, customRange, userTimezone],
  )
  const handlePeriodChange = useCallback(
    (newPeriod: ShopifyPeriod, newCustomRange?: { from: string; to: string }) => {
      setPeriod(newPeriod)
      setCustomRange(newCustomRange ?? null)
    },
    [],
  )
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(DEFAULT_COMPARISON_MODE)
  const [comparisonCustomRange, setComparisonCustomRange] = useState<{ from: string; to: string } | null>(null)
  const handleComparisonChange = useCallback(
    (newMode: ComparisonMode, newCustomRange?: { from: string; to: string }) => {
      setComparisonMode(newMode)
      setComparisonCustomRange(newCustomRange ?? null)
    },
    [],
  )
  const [isChannelDialogOpen, setIsChannelDialogOpen] = useState(false)
  const [configRevision, setConfigRevision] = useState(0)
  const [selectedProducts, setSelectedProducts] = useState<Map<number, ProductOption>>(new Map())
  const [productFilterOpen, setProductFilterOpen] = useState(false)
  const selectedProductIds = useMemo(() => Array.from(selectedProducts.keys()), [selectedProducts])
  const { can } = usePermissions()

  const {
    settings: shopifyAttributionSettings,
    updateSettings: updateShopifyAttributionSettings,
    loading: isSettingsLoading,
  } = useShopifyAttributionSettings()
  // The dashboard reads the stored model from the query cache; mount it once the first read settled.
  const modelPreference = useAttributionModelPreferenceQuery()

  // Auto-select first active integration when integrations load
  useEffect(() => {
    if (activeIntegrations.length > 0 && !selectedIntegration) {
      setSelectedIntegration(activeIntegrations[0].id)
    }
  }, [activeIntegrations, selectedIntegration])

  // Reset product filter when integration changes
  useEffect(() => {
    setSelectedProducts(new Map())
  }, [selectedIntegration])


  if (!can(SHOPIFY_PERMISSIONS.VIEW)) {
    return (
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                <span key={tUi("youDonTHavePermissionToAccessShopify")}>{tUi("youDonTHavePermissionToAccessShopify")}</span></AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    )
  }

  if ((isLoading && activeIntegrations.length === 0) || insightsAccess.loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2"><span key={tCommonUi("loading")}>{tCommonUi("loading")}</span></span>
      </div>
    )
  }

  if (activeIntegrations.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle><span key={tUi("shopifyDashboard")}>{tUi("shopifyDashboard")}</span></CardTitle>
            <CardDescription><span key={tUi("attributionAnalyticsAndChannelPerformance")}>{tUi("attributionAnalyticsAndChannelPerformance")}</span></CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <ShoppingBag className="h-4 w-4" />
              <AlertDescription>
                <span key={tUi("noActiveShopifyIntegrationsFoundPleaseConnectAnd")}>{tUi("noActiveShopifyIntegrationsFoundPleaseConnectAnd")}</span></AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    )
  }

  const view = dashboardView(insightsAccess.mode)

  return (
    <>
      <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight"><span key={tUi("shopifyDashboard")}>{tUi("shopifyDashboard")}</span></h1>
        <p className="text-sm text-muted-foreground"><span key={tUi("attributionAnalyticsAndChannelPerformance")}>{tUi("attributionAnalyticsAndChannelPerformance")}</span></p>
      </div>
      {view === "locked" && <InsightsLockedNotice mode={insightsAccess.mode} />}
      {view === "summary" && selectedIntegration && !isSettingsLoading && (
        <RevenueSummaryView
          integrationId={selectedIntegration}
          stores={activeIntegrations.map((i) => ({ id: i.id, shop_domain: i.shop_domain }))}
          onStoreChange={setSelectedIntegration}
          dateRange={dateRange}
          period={period}
          customRange={customRange}
          onPeriodChange={handlePeriodChange}
          minimumOrderTotal={shopifyAttributionSettings.minimum_order_total ?? 0}
          lockedMode={insightsAccess.mode}
        />
      )}
      {view === "full" && <InsightsTrialBanner mode={insightsAccess.mode} trialExpiresAt={insightsAccess.trialExpiresAt} />}
      {view === "full" && selectedIntegration && user && !isSettingsLoading && !modelPreference.isLoading && (
        <AttributionDashboard
          integrationId={selectedIntegration}
          userId={user.uuid ?? user.user_id}
          dateRange={dateRange}
          period={period}
          customRange={customRange}
          onPeriodChange={handlePeriodChange}
          comparisonMode={comparisonMode}
          comparisonCustomRange={comparisonCustomRange}
          onComparisonChange={handleComparisonChange}
          configRevision={configRevision}
          minimumOrderTotal={shopifyAttributionSettings.minimum_order_total ?? 0}
          onMinOrderSave={async (v) => { await updateShopifyAttributionSettings({ minimum_order_total: v }) }}
          isSettingsLoading={isSettingsLoading}
          canManage={can([INTEGRATIONS_PERMISSIONS.SHOPIFY_MANAGE])}
          onManageChannels={() => setIsChannelDialogOpen(true)}
          productIds={selectedProductIds}
          stores={activeIntegrations.map((i) => ({ id: i.id, shop_domain: i.shop_domain }))}
          selectedStore={selectedIntegration}
          onStoreChange={setSelectedIntegration}
          onProductFilterToggle={() => setProductFilterOpen((p) => !p)}
          productFilterCount={selectedProducts.size}
          productFilterOpen={productFilterOpen}
        />
      )}

      {view === "full" && selectedIntegration && (
        // Dialog stays mounted for the active integration so edits persist until the user closes it.
        <ChannelConfigDialog
          integrationId={selectedIntegration}
          open={isChannelDialogOpen}
          onOpenChange={setIsChannelDialogOpen}
          onConfigSaved={() => setConfigRevision((prev) => prev + 1)}
        />
      )}
      </div>

      {view === "full" && selectedIntegration && (
        <ProductFilterSideTab
          integrationId={selectedIntegration}
          dateRange={dateRange}
          minimumOrderTotal={shopifyAttributionSettings.minimum_order_total ?? 0}
          selectedProducts={selectedProducts}
          onSelectionChange={setSelectedProducts}
          open={productFilterOpen}
          onOpenChange={setProductFilterOpen}
        />
      )}
    </>
  )
}

/** Revenue and orders are on every plan; the rest of the dashboard needs Insights. */
function dashboardView(mode: ShopifyInsightsMode): "full" | "summary" | "locked" {
  switch (mode) {
    case "unlimited":
    case "trial_active":
      return "full"
    case "trial_expired":
    case "trial_not_started":
    case "not_available":
      return "summary"
    case "forbidden":
    case "error":
      return "locked"
  }
}
